import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CheckCircle2, User, Phone, Home, Landmark } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function OnboardingPage() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    phone: "", personal_email: "", address: "",
    emergency_contact_name: "", emergency_contact_phone: "",
    pan: "", aadhaar: "",
    bank_name: "", bank_account: "", bank_ifsc: "", account_holder_name: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/me/profile").then(({ data }) => {
      setProfile(data.employee);
      // If already onboarded, kick back to dashboard
      if (data.employee?.onboarded_at) nav("/", { replace: true });
      setForm((f) => ({ ...f, account_holder_name: data.employee?.name || "" }));
    }).catch(() => {});
  }, []);

  if (user === false) return <Navigate to="/login" replace />;
  if (user?.role !== "employee") return <Navigate to="/" replace />;

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.bank_account && !form.account_holder_name.trim()) {
      toast.error("Account holder name is required when bank account is provided");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, bank_ifsc: form.bank_ifsc ? form.bank_ifsc.toUpperCase() : null, pan: form.pan ? form.pan.toUpperCase() : null };
      await api.patch("/me/profile", payload);
      toast.success("Profile submitted — bank & KYC details are pending admin approval");
      await refresh();
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Welcome, {profile?.name?.split(" ")[0] || "there"}</h1>
          <p className="mt-2 text-sm text-zinc-500">
            Complete your profile so HR can process your payroll. Bank details, PAN and Aadhaar will go to admin for approval.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="onboarding-form">
          <Card className="border-zinc-200 shadow-none">
            <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base font-semibold"><User className="h-4 w-4 text-emerald-600" /> Personal contact</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Mobile number</Label><Input value={form.phone} onChange={change("phone")} className="h-9" placeholder="+91 …" required data-testid="onb-phone" /></div>
              <div><Label className="text-xs">Personal email</Label><Input type="email" value={form.personal_email} onChange={change("personal_email")} className="h-9" placeholder="you@gmail.com" required data-testid="onb-personal-email" /></div>
              <div className="col-span-2"><Label className="text-xs">Current address</Label><Textarea rows={2} value={form.address} onChange={change("address")} required data-testid="onb-address" /></div>
            </CardContent>
          </Card>

          <Card className="border-zinc-200 shadow-none">
            <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base font-semibold"><Phone className="h-4 w-4 text-emerald-600" /> Emergency contact</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Full name</Label><Input value={form.emergency_contact_name} onChange={change("emergency_contact_name")} className="h-9" required data-testid="onb-ec-name" /></div>
              <div><Label className="text-xs">Mobile number</Label><Input value={form.emergency_contact_phone} onChange={change("emergency_contact_phone")} className="h-9" placeholder="+91 …" required data-testid="onb-ec-phone" /></div>
            </CardContent>
          </Card>

          <Card className="border-zinc-200 shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base font-semibold"><Landmark className="h-4 w-4 text-emerald-600" /> KYC & Bank details</CardTitle>
              <p className="text-xs text-amber-700 mt-1">These fields need admin approval before payroll uses them.</p>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">PAN</Label><Input value={form.pan} onChange={change("pan")} className="h-9 uppercase" placeholder="ABCDE1234F" pattern="[A-Za-z]{5}[0-9]{4}[A-Za-z]" maxLength={10} required data-testid="onb-pan" /></div>
              <div><Label className="text-xs">Aadhaar</Label><Input value={form.aadhaar} onChange={change("aadhaar")} className="h-9" placeholder="12 digits" pattern="\d{12}" maxLength={12} required data-testid="onb-aadhaar" /></div>
              <div><Label className="text-xs">Bank name</Label><Input value={form.bank_name} onChange={change("bank_name")} className="h-9" placeholder="HDFC Bank" required data-testid="onb-bank-name" /></div>
              <div><Label className="text-xs">IFSC</Label><Input value={form.bank_ifsc} onChange={change("bank_ifsc")} className="h-9 uppercase" placeholder="HDFC0001234" pattern="[A-Za-z]{4}0[A-Za-z0-9]{6}" maxLength={11} required data-testid="onb-ifsc" /></div>
              <div><Label className="text-xs">Bank account number</Label><Input value={form.bank_account} onChange={change("bank_account")} className="h-9" placeholder="9–18 digits" pattern="\d{9,18}" maxLength={18} required data-testid="onb-bank-account" /></div>
              <div><Label className="text-xs">Account holder name</Label><Input value={form.account_holder_name} onChange={change("account_holder_name")} className="h-9" placeholder="As per bank" required data-testid="onb-holder" /></div>
            </CardContent>
          </Card>

          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-zinc-500">All fields are required to complete onboarding.</p>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 h-10 press-scale" data-testid="onb-submit">
              <CheckCircle2 className="h-4 w-4 mr-2" /> {saving ? "Submitting…" : "Submit for review"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
